import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftRight,
  Banknote,
  Receipt,
  SearchIcon,
  Store,
  Webhook,
  type LucideIcon,
} from "lucide-react";

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { errorMessage } from "~/lib/api";
import { useSearch, type SearchResult, type SearchResultType } from "~/lib/queries";
import { cn } from "~/lib/utils";

/** The server's own minimum. Below it no request is made at all. */
const MIN_QUERY = 3;

/**
 * Long enough that a paste-and-read does not fire a query per keystroke, short
 * enough that it still feels like it is keeping up.
 */
const DEBOUNCE_MS = 200;

/**
 * Paste any identifier, land on the right page.
 *
 * Ops arrives with one string out of a Slack thread and no idea which table it
 * belongs to. Before this, that meant guessing which screen to open; now the
 * identifier is the entry point.
 *
 * Exact and prefix matches only, server-side — people paste identifiers here,
 * they do not guess at spellings, so there is no fuzzy matching to be
 * disappointed by. A single unambiguous match navigates straight there; anything
 * else is a grouped list.
 */
export function GlobalSearch() {
  const [open, setOpen] = useState(false);

  // ⌘K / Ctrl-K from anywhere, and Escape closes through the dialog itself.
  // Bound on the window rather than on the trigger so it works while focus is in
  // a table, which is where it will usually be.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
      // Do not steal the browser's own shortcut when a text field has focus and
      // the user is plainly typing rather than reaching for search.
      event.preventDefault();
      setOpen((value) => !value);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-8 w-full max-w-sm items-center gap-2 rounded-lg border border-input bg-transparent px-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none dark:bg-input/30"
      >
        <SearchIcon className="size-3.5 shrink-0" />
        <span className="truncate">Search any identifier…</span>
        <kbd className="ml-auto hidden shrink-0 rounded border bg-muted px-1.5 py-0.5 font-mono text-[0.6875rem] sm:inline">
          ⌘K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="top-24 max-w-xl translate-y-0 gap-0 p-0" showClose={false}>
          <SearchPanel onNavigate={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function SearchPanel({ onNavigate }: { onNavigate: () => void }) {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced, so pasting a 30-character order id is one request rather than
  // thirty — each of which fans out across five indexed lookups server-side.
  useEffect(() => {
    const id = setTimeout(() => setQuery(input), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [input]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const { data, error, isFetching } = useSearch(query);
  const results = useMemo(() => data?.results ?? [], [data]);

  function go(result: SearchResult) {
    onNavigate();
    void navigate(routeFor(result));
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    // Enter takes the single unambiguous match, which is the whole point when
    // someone pastes an order id. With several results it does nothing rather
    // than guessing at the first.
    if (event.key === "Enter" && results.length === 1 && results[0]) {
      event.preventDefault();
      go(results[0]);
    }
  }

  const short = query.trim().length > 0 && query.trim().length < MIN_QUERY;

  return (
    <div>
      <DialogTitle className="sr-only">Search</DialogTitle>
      <DialogDescription className="sr-only">
        Search transactions, bridge orders, webhook events, storefronts and settlements by
        identifier.
      </DialogDescription>

      <div className="flex items-center gap-2 border-b px-3">
        <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Order ID, reference no, email, key prefix, settlement ID…"
          autoComplete="off"
          spellCheck={false}
          className="h-11 border-0 bg-transparent px-0 focus-visible:ring-0 dark:bg-transparent"
        />
        {isFetching ? (
          <span className="shrink-0 text-xs text-muted-foreground">…</span>
        ) : null}
      </div>

      <div className="max-h-[22rem] overflow-y-auto p-2">
        {error ? (
          <Message>{errorMessage(error)}</Message>
        ) : short ? (
          <Message>Keep typing — at least {MIN_QUERY} characters.</Message>
        ) : query.trim().length === 0 ? (
          <Hints />
        ) : results.length === 0 && !isFetching ? (
          <Message>
            Nothing matches that. Search is exact and prefix only — it does not guess at
            spellings, so a partial identifier from the middle of a string will not match.
          </Message>
        ) : (
          <Results results={results} onSelect={go} truncated={data?.truncated ?? false} />
        )}
      </div>
    </div>
  );
}

function Results({
  results,
  onSelect,
  truncated,
}: {
  results: SearchResult[];
  onSelect: (result: SearchResult) => void;
  truncated: boolean;
}) {
  // Grouped by type, in the order the groups are listed below rather than the
  // order the server happened to return them.
  const groups = GROUP_ORDER.map((type) => ({
    type,
    items: results.filter((result) => result.type === type),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.type}>
          <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
            {GROUP_LABELS[group.type]}
          </p>
          <ul>
            {group.items.map((result) => (
              <li key={`${result.type}-${result.id}`}>
                <button
                  type="button"
                  onClick={() => onSelect(result)}
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
                >
                  <ResultIcon type={result.type} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-mono text-[0.8125rem]">
                      {result.label}
                    </span>
                    {result.sublabel ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {result.sublabel}
                      </span>
                    ) : null}
                  </span>
                  {/* Which column matched. "found by reference_no" is a
                      different fact from "found by order_id", and the person
                      searching usually knows which they pasted. */}
                  <span className="shrink-0 font-mono text-[0.6875rem] text-muted-foreground">
                    {result.matched_on}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {truncated ? (
        <p className="px-2 pb-1 text-xs text-muted-foreground">
          Only the first few matches per type are shown. Open the relevant screen to filter
          properly.
        </p>
      ) : null}
    </div>
  );
}

/** What can be pasted, said plainly, because none of it is discoverable. */
function Hints() {
  return (
    <div className="space-y-1.5 p-2 text-xs text-muted-foreground">
      <p className="font-medium text-foreground">Paste any of these:</p>
      <ul className="space-y-1">
        <li>
          <code className="font-mono">order_id</code> or a DurianPay{" "}
          <code className="font-mono">reference_no</code> → the transaction
        </li>
        <li>a Shopify order id or name (#1042) → the bridge order</li>
        <li>a customer email → that customer's bridge orders</li>
        <li>
          an <code className="font-mono">external_id</code> or order ref → webhook events
        </li>
        <li>
          a key prefix (<code className="font-mono">alt_live_…</code>) → the owning storefront
        </li>
        <li>a merchant name or shop domain → the storefront</li>
        <li>
          a <code className="font-mono">psp_settlement_id</code> → the settlement
        </li>
      </ul>
    </div>
  );
}

function Message({ children }: { children: React.ReactNode }) {
  return <p className="p-3 text-sm text-muted-foreground">{children}</p>;
}

const GROUP_ORDER: SearchResultType[] = [
  "transaction",
  "bridge_order",
  "webhook_event",
  "storefront",
  "settlement",
];

const GROUP_LABELS: Record<SearchResultType, string> = {
  transaction: "Transactions",
  bridge_order: "Bridge orders",
  webhook_event: "Webhook events",
  storefront: "Storefronts",
  settlement: "Settlements",
};

/** The same icons the nav uses, so a result looks like where it will land. */
const ICONS: Record<SearchResultType, LucideIcon> = {
  transaction: Receipt,
  bridge_order: ArrowLeftRight,
  webhook_event: Webhook,
  storefront: Store,
  settlement: Banknote,
};

function ResultIcon({ type }: { type: SearchResultType }) {
  const Icon = ICONS[type];
  return <Icon className={cn("size-4 shrink-0 text-muted-foreground")} />;
}

/**
 * Where each result type lives.
 *
 * A discriminated switch rather than a template string, so adding a result type
 * on the server without a route here is a type error rather than a dead link.
 */
function routeFor(result: SearchResult) {
  switch (result.type) {
    case "transaction":
      return { to: "/transactions/$orderId", params: { orderId: result.id } } as const;
    case "bridge_order":
      return { to: "/bridge/$shopifyOrderId", params: { shopifyOrderId: result.id } } as const;
    case "webhook_event":
      return { to: "/webhook-events/$eventId", params: { eventId: result.id } } as const;
    case "storefront":
      return { to: "/storefronts/$storefrontId", params: { storefrontId: result.id } } as const;
    case "settlement":
      return { to: "/settlements/$settlementId", params: { settlementId: result.id } } as const;
  }
}
